import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { ChevronRightRounded, RemoveRedEyeRounded, ShareRounded, ThumbUpRounded } from '@mui/icons-material';

import { INLINE_CHANNEL_INFO_ID } from '../../../common/components/CustomInjectionBlock/constants';
import { channelPropTypes } from '../../../common/constants/propTypes';
import { DEFAULT_VIEW_PATH } from '../../../common/constants/routePath';
import { LIKES, SHARES, VIEWS } from '../../../common/constants/socialStatistic';
import { MAIN_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { VIEW_ALL } from '@/modules/common/components/Player/constants/customEvents';

import { isWatchInIframe } from '../../../common/helpers/routes';
import { abbreviateNumber, getViewSocialStatisticData } from '../../../common/helpers/video';
import * as commonSelectors from '../../../common/redux/selectors/common';
import { usePlayerWidget } from '@/modules/common/components/Player/helpers';

import CustomInjectionBlock from '../../../common/components/CustomInjectionBlock/CustomInjectionBlock';
import SocialButtons from '../../../common/components/SocialButtons/SocialButtons';
import Carousel, { Skeleton as CarouselSkeleton } from '../Carousel/Carousel';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import { BrowserLink, Link, Stack, TruncatedText, Typography } from '@/mui/components';

import styles from './InlineCarousel.module.scss';

function InlineCarousel(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const carouselOptionsRef = useRef(null);
  const engagement = useSelector(commonSelectors.engagementSelector);
  const dblClickFlow = useSelector(commonSelectors.dblClickFlowSelector);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);

  const selectedVideo = props.channel.playlist[selectedIndex];

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.channel.playlist]);

  const iconsCollection = {
    [VIEWS]: RemoveRedEyeRounded,
    [LIKES]: ThumbUpRounded,
    [SHARES]: ShareRounded,
  };

  const socialStatistic = selectedVideo
    ? getViewSocialStatisticData(selectedVideo.performanceData.parameters, engagement)
    : {};

  return (
    <div
      className={styles.Wrapper}
      data-a="channel-type-inline"
      aria-label="Videos by channel"
      style={{
        '--sc-bg-color': props.bg,
        '--sc-logo-bg-color': props.bgLogo,
        '--sc-title-color': props.titleColor,
        '--sc-desc-color': props.descColor,
        '--sc-vid-title-color': props.vidTitleColor,
        '--sc-vid-info-color': props.vidInfoColor,
      }}
    >
      <div className={styles.Container}>
        <div className={styles.VideoWrapper} data-a="player-video-wrapper">
          {selectedVideo && (
            <VideoPlayer
              video={selectedVideo}
              channel={props.channel}
              onEnded={() => {
                const nextPossibleIndex = selectedIndex + 1;
                const nextVideoIndex = nextPossibleIndex >= props.channel.playlist.length ? 0 : nextPossibleIndex;

                setSelectedIndex(nextVideoIndex);

                carouselOptionsRef.current?.scrollTo(nextVideoIndex);
              }}
            />
          )}
        </div>
        <div className={styles.InfoWrapper}>
          <div className={styles.Info}>
            <CustomInjectionBlock id={INLINE_CHANNEL_INFO_ID} />
            {selectedVideo && (
              <Stack spacing={4}>
                <div data-a="video-title">
                  <TruncatedText title={selectedVideo.title} rows={3}>
                    <Typography variant="h6">{selectedVideo.title}</Typography>
                  </TruncatedText>
                </div>
                <Stack spacing={4} direction="row" alignItems="center">
                  <Stack spacing={2} direction="row" alignItems="center">
                    <Typography variant="body2" data-a="video-date-time">
                      {selectedVideo.createdFormatted}
                    </Typography>
                    <Typography variant="body2" data-a="video-duration">
                      {selectedVideo.durationFormatted}
                    </Typography>
                  </Stack>
                  {!!Object.keys(socialStatistic).length && (
                    <Stack spacing={4} direction="row" alignItems="center" data-a="social-statistic">
                      {Object.keys(socialStatistic).map((type) => {
                        const Icon = iconsCollection[type];

                        return (
                          <Stack key={type} spacing={1} direction="row" alignItems="center">
                            <Icon fontSize="small" />
                            <Typography variant="body2">{abbreviateNumber(socialStatistic[type])}</Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
                {!isWatchInIframe && (
                  <SocialButtons
                    video={selectedVideo}
                    routePath={DEFAULT_VIEW_PATH}
                    channelId={props.channel.id}
                    channelType={props.channel.type}
                    fromInlineChannel
                  />
                )}
                <div data-a="video-description">
                  <TruncatedText title={selectedVideo.plot} rows={10}>
                    <Typography variant="body2">{selectedVideo.plot}</Typography>
                  </TruncatedText>
                </div>
              </Stack>
            )}
          </div>
        </div>
        <div className={styles.WrapperCarousel}>
          <Stack spacing={2} direction="row" alignContent="center" alignItems="center" className={styles.TitleWrapper}>
            <Link
              data-a="channel-title"
              color="inherit"
              component={BrowserLink}
              to={`/channel/${props.channel.id}${location.search}`}
              className={styles.Title}
            >
              <TruncatedText title={props.channel.title} rows={1}>
                {props.channel.title}
              </TruncatedText>
            </Link>
            <Link
              color="inherit"
              component={BrowserLink}
              to={`/channel/${props.channel.id}${location.search}`}
              title={`All ${props.channel.title} videos`}
              data-a="channel-title-view-all"
              onClick={() => {
                LWP.sendCustomEvent(VIEW_ALL);
              }}
            >
              <Stack direction="row" alignItems="flex-end">
                <Typography variant="body2" fontWeight={400} noWrap>
                  View All
                </Typography>
                <ChevronRightRounded fontSize="medium" />
              </Stack>
            </Link>
          </Stack>
          <div className={styles.Carousel}>
            {props.channel.playlist.length ? (
              <Carousel
                ref={carouselOptionsRef}
                channel={props.channel}
                activeSlideMediaId={selectedVideo?.mediaid}
                onSlideClickFn={(event, mediaId) => {
                  if (dblClickFlow && selectedVideo?.mediaid !== mediaId) {
                    event.preventDefault();
                  }

                  const index = props.channel.playlist.findIndex((video$) => video$.mediaid === mediaId);

                  setSelectedIndex(index !== -1 ? index : 0);
                }}
                onSlideDoubleClickFn={(_, mediaId) => {
                  if (dblClickFlow && selectedVideo?.mediaid !== mediaId) {
                    navigate(`/channel/${props.channel.id}/${mediaId}${location.search}`);
                  }
                }}
              />
            ) : (
              <CarouselSkeleton channel={props.channel} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

InlineCarousel.propTypes = {
  bg: PropTypes.string.isRequired,
  bgLogo: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  descColor: PropTypes.string.isRequired,
  vidTitleColor: PropTypes.string.isRequired,
  vidInfoColor: PropTypes.string.isRequired,
  channel: channelPropTypes.isRequired,
};

export default InlineCarousel;
